import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { toast } from "@/components/ui/sonner";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export function CheckoutDialog({ open, onOpenChange }) {
  const { cart, subtotal, clearCart, paymentConfig, refreshProducts } = useStore();
  const [form, setForm] = useState({ name: "", email: "", address: "" });
  const [method, setMethod] = useState("demo");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const total = subtotal + (subtotal >= 20000 || subtotal === 0 ? 0 : 499);

  const methods = [
    { id: "demo", label: "Demo checkout", enabled: paymentConfig.demo, note: "Saves order instantly" },
    { id: "razorpay", label: "Razorpay", enabled: paymentConfig.razorpay, note: "UPI, cards, netbanking" },
    { id: "paypal", label: "PayPal", enabled: paymentConfig.paypal, note: "International payments" },
  ];

  const orderPayload = (paymentMethod) => ({
    customer: form,
    payment_method: paymentMethod,
    items: cart.map((item) => ({ product_id: item.id, quantity: item.quantity })),
  });

  const finishOrder = (created) => {
    setOrder(created);
    clearCart();
    refreshProducts();
  };

  useEffect(() => {
    if (method !== "paypal" || !paymentConfig.paypal || !open || order) return undefined;
    let cancelled = false;
    const renderButtons = async () => {
      await loadScript(`https://www.paypal.com/sdk/js?client-id=${paymentConfig.paypal_client_id}&currency=INR`);
      if (cancelled || !window.paypal) return;
      const container = document.getElementById("paypal-button-container");
      if (!container || container.childNodes.length) return;
      window.paypal.Buttons({
        createOrder: async () => {
          const { data } = await api.post("/payments/paypal/order", orderPayload("paypal"));
          return data.id;
        },
        onApprove: async (data) => {
          try {
            const { data: created } = await api.post(`/payments/paypal/capture/${data.orderID}`);
            finishOrder(created);
            toast.success("PayPal payment captured");
          } catch (error) {
            toast.error(formatApiError(error));
          }
        },
        onError: () => toast.error("PayPal payment failed"),
      }).render("#paypal-button-container");
    };
    renderButtons().catch(() => toast.error("PayPal failed to load"));
    return () => { cancelled = true; };
  }, [method, paymentConfig, open, order]);

  const payWithRazorpay = async () => {
    await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    const { data } = await api.post("/payments/razorpay/order", orderPayload("razorpay"));
    const razorpay = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      order_id: data.order_id,
      name: "BKG Global",
      description: "BKG Global order",
      prefill: { name: form.name, email: form.email },
      theme: { color: "#CCFF00" },
      handler: async (response) => {
        try {
          const { data: created } = await api.post("/payments/razorpay/verify", response);
          finishOrder(created);
          toast.success("Payment verified");
        } catch (error) {
          toast.error(formatApiError(error));
        }
      },
    });
    razorpay.open();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (method === "paypal") return;
    setLoading(true);
    try {
      if (method === "razorpay") {
        await payWithRazorpay();
      } else {
        const { data } = await api.post("/orders", orderPayload("demo"));
        finishOrder(data);
        toast.success("Order confirmed");
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const close = (value) => {
    onOpenChange(value);
    if (!value) setOrder(null);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="border-white/10 bg-void text-foreground" data-testid="checkout-dialog">
        {order ? (
          <div className="py-8 text-center" data-testid="order-confirmation">
            <p className="text-xs uppercase tracking-[0.28em] text-volt">Order confirmed</p>
            <DialogTitle className="mt-4 font-heading text-5xl font-black italic">{order.order_number}</DialogTitle>
            <p className="mt-5 text-zinc-400">A confirmation has been saved for {order.customer.email}. Total paid: {formatCurrency(order.total)}.</p>
            <Button data-testid="continue-shopping-button" onClick={() => close(false)} className="kinetic-button mt-8 rounded-none bg-volt text-black hover:bg-transparent hover:text-volt">Continue shopping</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-4xl font-black italic">Checkout</DialogTitle>
              <DialogDescription className="text-zinc-500">Choose how you want to pay. Methods are managed from the admin studio.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5">
              <Input data-testid="checkout-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-none border-x-0 border-t-0 bg-transparent" />
              <Input data-testid="checkout-email-input" required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-none border-x-0 border-t-0 bg-transparent" />
              <Textarea data-testid="checkout-address-input" required placeholder="Delivery address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="min-h-24 rounded-none border-x-0 border-t-0 bg-transparent" />
              <div className="grid gap-3 sm:grid-cols-3">
                {methods.map((item) => (
                  <button key={item.id} type="button" data-testid={`payment-method-${item.id}`} disabled={!item.enabled} onClick={() => setMethod(item.id)} className={`border p-4 text-left transition-colors ${method === item.id ? "border-volt bg-volt/10 text-volt" : "border-white/10 text-zinc-400"} disabled:cursor-not-allowed disabled:opacity-40`}>
                    <span className="block text-sm font-bold">{item.label}</span>
                    <span className="mt-1 block text-xs">{item.enabled ? item.note : "Enable in admin"}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span className="text-sm uppercase tracking-[0.22em] text-zinc-500">Total</span>
                <span data-testid="checkout-total" className="font-subheading text-3xl font-bold text-volt">{formatCurrency(total)}</span>
              </div>
              {method === "paypal" ? (
                <div id="paypal-button-container" data-testid="paypal-button-container" className="min-h-12" />
              ) : (
                <Button data-testid="place-order-button" disabled={loading || cart.length === 0} className="kinetic-button h-12 w-full rounded-none bg-volt font-bold uppercase tracking-[0.2em] text-black hover:bg-transparent hover:text-volt">
                  {loading ? "Processing..." : method === "razorpay" ? "Pay with Razorpay" : "Place order"}
                </Button>
              )}
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
